// Notification sounds and vibration alerts

// Sound URLs - using web audio or fallback to Audio element
const NOTIFICATION_SOUNDS = {
  newJob: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', // Cheerful notification
  jobAccepted: 'https://assets.mixkit.co/active_storage/sfx/2867/2867-preview.mp3', // Success bell
  message: 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3', // Message pop
  payment: 'https://assets.mixkit.co/active_storage/sfx/2863/2863-preview.mp3', // Coin sound
  alert: 'https://assets.mixkit.co/active_storage/sfx/2872/2872-preview.mp3' // Alert
};

// Vibration patterns (in milliseconds)
const VIBRATION_PATTERNS = {
  newJob: [200, 100, 200, 100, 200], // Triple pulse for new job
  jobAccepted: [300, 100, 300], // Double pulse for acceptance
  message: [100], // Single short vibrate
  payment: [100, 50, 100, 50, 100], // Quick pulses for payment
  alert: [500, 200, 500] // Strong alert
};

// Audio context for better sound handling
let audioContext = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
}

// Play notification sound
export async function playNotificationSound(type = 'message') {
  try {
    const soundUrl = NOTIFICATION_SOUNDS[type] || NOTIFICATION_SOUNDS.message;
    
    // Try Web Audio API first for better control
    try {
      const ctx = getAudioContext();
      const response = await fetch(soundUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      
      // Add gain control for volume
      const gainNode = ctx.createGain();
      gainNode.gain.value = 0.5; // 50% volume
      
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      source.start(0);
      
      return true;
    } catch (webAudioError) {
      // Fallback to Audio element
      const audio = new Audio(soundUrl);
      audio.volume = 0.5;
      await audio.play();
      return true;
    }
  } catch (error) {
    console.warn('Failed to play notification sound:', error);
    return false;
  }
}

// Trigger vibration
export function vibrate(type = 'message') {
  if (!('vibrate' in navigator)) {
    console.log('Vibration not supported');
    return false;
  }
  
  try {
    const pattern = VIBRATION_PATTERNS[type] || VIBRATION_PATTERNS.message;
    navigator.vibrate(pattern);
    return true;
  } catch (error) {
    console.warn('Vibration failed:', error);
    return false;
  }
}

// Combined notification with sound and vibration
export function notifyUser(type = 'message', options = {}) {
  const { sound = true, vibration = true } = options;
  
  const results = {
    sound: false,
    vibration: false
  };
  
  if (sound) {
    results.sound = playNotificationSound(type);
  }
  
  if (vibration) {
    results.vibration = vibrate(type);
  }
  
  return results;
}

// Show browser notification (if permission granted)
export async function showBrowserNotification(title, options = {}) {
  if (!('Notification' in window)) {
    console.log('Browser notifications not supported');
    return null;
  }
  
  // Request permission if not granted
  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }
  
  if (Notification.permission !== 'granted') {
    console.log('Notification permission denied');
    return null;
  }
  
  try {
    const notification = new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: VIBRATION_PATTERNS[options.type || 'message'],
      ...options
    });
    
    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    return notification;
  } catch (error) {
    console.warn('Failed to show notification:', error);
    return null;
  }
}

// Request notification permissions
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

// Hook for notification settings
export function useNotificationSettings() {
  const getSettings = () => {
    const stored = localStorage.getItem('notificationSettings');
    return stored ? JSON.parse(stored) : { sound: true, vibration: true };
  };
  
  const saveSettings = (settings) => {
    localStorage.setItem('notificationSettings', JSON.stringify(settings));
  };
  
  return { getSettings, saveSettings };
}

export default {
  playNotificationSound,
  vibrate,
  notifyUser,
  showBrowserNotification,
  requestNotificationPermission
};
